import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'

// A ship point can be a recognized place (facility/club/resort by `id`) or a
// raw address. Mirrors the GraphQL `ShipPointInput` (Types::ShipPointInputType).
export interface PricingShipPointInput {
  id?: string
  address1?: string
  address2?: string
  city?: string
  state?: string
  postalCode?: string
  countryCode?: string
  facilityId?: string
  addressType?: string
}

export interface PricingRatesInput {
  origin: PricingShipPointInput
  destination: PricingShipPointInput
}

interface PricingProductField {
  productLine: string
  productLineFilterKey: string
  priceCents: number
}

export interface PricingRate {
  name: string
  products: PricingProductField[]
}

const PRICING_RATES_QUERY = gql`
  query GetPricingRates($shipRoute: ShipRouteInput!) {
    getPricingRates(shipRoute: $shipRoute) {
      name
      products {
        productLine
        productLineFilterKey
        priceCents
      }
    }
  }
`

/**
 * Lowest Luggage price (in cents) across all returned rate/product options, or
 * null if none. Matches the partner-page BookingWidget logic: it scans every
 * rate's products for the "Luggage" product line and takes the minimum.
 */
export const lowestLuggagePriceCents = (rates: PricingRate[] | undefined): number | null => {
  if (!rates?.length) return null

  let lowest = Infinity
  for (const rate of rates) {
    for (const product of rate.products ?? []) {
      const line = product.productLineFilterKey || product.productLine
      if (line === 'Luggage' && product.priceCents && product.priceCents < lowest) {
        lowest = product.priceCents
      }
    }
  }

  return lowest < Infinity ? lowest : null
}

/**
 * GraphQL `getPricingRates` — the starting-price estimate for a route, keyed on
 * origin/destination only (no dates/products). Used by the Ship&Play home
 * booking widget to show a dynamic "Starting at $X each way".
 */
export const usePricingRatesApi = (
  input: PricingRatesInput | undefined,
): UseQueryResult<PricingRate[], Error> => {
  const client = useGraphQLClient()

  const enabled = !!input?.origin && !!input?.destination

  return useQuery<PricingRate[], Error>({
    enabled,
    queryKey: ['pricingRates', input],
    queryFn: () =>
      client
        .request<{ getPricingRates: PricingRate[] }>(PRICING_RATES_QUERY, {
          shipRoute: input,
        })
        .then((data) => data.getPricingRates),
  })
}
